import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/articles';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.categorySlug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950">频道未找到</h1>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `17吃瓜网 - ${category.name}`,
        description: `${category.name} - ${category.description}，17吃瓜网为您实时追踪最新资讯。`,
        url: `https://17chigua.com/category/${slug}`,
        isPartOf: { '@id': 'https://17chigua.com/#website' },
        about: { '@type': 'Thing', name: category.name },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, item: { '@id': 'https://17chigua.com/', name: '首页' } },
          { '@type': 'ListItem', position: 2, item: { '@id': `https://17chigua.com/category/${slug}`, name: category.name } },
        ],
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <SEOHead
        title={`${category.name} | 17吃瓜网 - ${category.description}`}
        description={`17吃瓜网${category.name}频道 - ${category.description}，每日更新最新${category.name}爆料、视频和深度报道。`}
        keywords={`17吃瓜网,${category.name},吃瓜爆料,网红黑料,在线视频`}
        canonicalUrl={`https://17chigua.com/category/${slug}`}
      />
      <Navbar />

      <main className="pt-16">
        {/* Category Hero */}
        <section className="relative w-full min-h-[350px] md:min-h-[400px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20coral%20pink%20gradient%20with%20dark%20teal%20accents%2C%20soft%20geometric%20shapes%2C%20modern%20editorial%20design%2C%20clean%20composition%2C%20subtle%20texture%2C%20cinematic%20atmosphere%2C%20luxurious%20minimal%20background%20with%20flowing%20organic%20curves%2C%20no%20text%20no%20people&width=1920&height=800&seq=category-hero-bg&orientation=landscape"
              alt={category.name}
              title={`${category.name} - 17吃瓜网`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>

          <div className="relative z-10 w-full px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-[42%] text-center lg:text-left">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-primary-500/30 mb-4 mx-auto lg:mx-0">
                  <i className={`${category.iconClass} text-2xl text-background-50`}></i>
                </div>
                <h1 className="text-3xl md:text-5xl font-heading font-black text-background-50">
                  {category.name}
                </h1>
                <p className="mt-3 text-base text-background-50/70 leading-relaxed">
                  {category.description}
                </p>
              </div>
              <div className="lg:w-[58%] hidden lg:block"></div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="px-4 md:px-6 py-4 border-b border-background-200 bg-background-50">
          <Breadcrumb items={[{ label: '首页', href: '/' }, { label: category.name }]} />
        </div>

        {/* Articles */}
        <section className="py-10 md:py-16 px-4 md:px-6">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
              全部{category.name}
              <span className="ml-2 text-sm font-label font-normal text-foreground-400">
                共 {categoryArticles.length} 篇
              </span>
            </h2>
          </div>

          {categoryArticles.length > 0 ? (
            <div className="flex gap-5 overflow-x-auto pb-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {categoryArticles.map((article, index) => (
                <ArticleCard key={article.id} article={article} showTag={index === 0} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500">该频道暂无内容，敬请期待</p>
            </div>
          )}
        </section>

        {/* Other Categories */}
        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 text-center mb-8">
            探索更多频道
          </h2>
          <div className="flex flex-wrap justify-center gap-3 max-w-[900px] mx-auto">
            {categories.filter((c) => c.slug !== slug).map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-4 py-2 bg-background-50 border border-background-200 text-foreground-700 font-label text-sm rounded-lg hover:border-primary-400 hover:text-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}